import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { appContext } from "../../context/ContextProvider";
import {
  ConnectNavigations,
  navigation,
  superAdminNavigation,
} from "../../router/routes";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  InputBase,
  IconButton,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { PlusCircleIcon } from "@heroicons/react/20/solid";

function SideBar({ sidebarOpen, setSidebarOpen, setSubNav }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir && i18n.dir() === "rtl";
  const { user } = useContext(appContext);
  const [active, setActive] = useState("/dashboard");
  const [roleNavigation, setRoleNavigation] = useState([]);

  useEffect(() => {
    setActive(location?.pathname);
  }, [location]);

  useEffect(() => {
    const localUser = JSON.parse(localStorage.getItem("user"));
    const currentRoleId = user?.RoleId || localUser?.RoleId;

    if (currentRoleId == "12") {
      setRoleNavigation(superAdminNavigation);
    } else if (currentRoleId == "6") {
      // Connect role for now dummy
      setRoleNavigation(ConnectNavigations);
    } else {
      setRoleNavigation(navigation);
    }
  }, [user?.RoleId, sidebarOpen]);

  const primaryColor = "#ffffff";
  const activeBgColor = "#1faae8"; // Similar to the active light blue highlight from image
  const activeTextColor = primaryColor;
  const hoverBgColor = "rgba(255, 255, 255, 0.1)";
  const textColor = "#ffffff";

  const handleNavClick = (nav) => {
    if (nav?.menu) {
      navigate("/" + nav.menu[0].path);
      localStorage.setItem("subMenuNav", JSON.stringify(nav.menu));
      setSubNav(nav.menu);
    } else {
      setSubNav([]);
      localStorage.setItem("subMenuNav", JSON.stringify([]));
      navigate("/" + nav?.path);
    }
  };
  useEffect(() => {
    const matchedNav = roleNavigation.find((nav) => {
      if (nav.menu) {
        return nav.menu.some((item) =>
          `/${item.path}` === location.pathname ||
          (item.path === "claims/bulk-replace" && location.pathname.includes("bulk-replace"))
        );
      }
      return `/${nav.path}` === location.pathname;
    });
    if (matchedNav) {
      if (matchedNav.menu) {
        setSubNav(matchedNav.menu);
        localStorage.setItem("subMenuNav", JSON.stringify(matchedNav.menu));
      } else {
        setSubNav([]);
        localStorage.setItem("subMenuNav", JSON.stringify([]));
      }
    }
  }, [location.pathname, roleNavigation]);

  const handleCreateClick = (e, subNav) => {
    e.stopPropagation();

    // Find the parent navigation based on the path
    const targetPath = subNav.path.split("/")[0]; // Get the base path (e.g., 'beneficiary' from 'beneficiary/form')
    const matchedNav = roleNavigation.find((nav) => nav.path === targetPath);

    // Update the subNav state to reflect the correct tabs
    if (matchedNav && matchedNav.menu) {
      setSubNav(matchedNav.menu);
      localStorage.setItem("subMenuNav", JSON.stringify(matchedNav.menu));
    }

    navigate(`/${subNav.path}`);
  };

  return (
    <Box
      sx={{
        width: sidebarOpen ? 210 : 100,
        height: "100vh",
        bgcolor: "#0070C2",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.3s ease",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          py: 1.5,
          px: sidebarOpen ? 1.5 : 0.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "50px",
        }}
      >
        <Box
          sx={{
            width: sidebarOpen ? "45px" : "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src="/images/supergit-4.0.png"
            alt="logo"
            style={{ width: "100%", objectFit: "contain" }}
          />
          {sidebarOpen && (
            <span className="text-white ml-2 text-lg font-bold">SuperGit</span>
          )}
        </Box>
      </Box>

      {/* Navigation List Area */}
      <Box
        sx={{
          overflowY: "auto",
          flexGrow: 1,
          mt: 0,
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "rgba(255, 255, 255, 0.1)",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255, 255, 255, 0.5)",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "rgba(255, 255, 255, 0.8)",
          },
        }}
      >
        <List
          component="nav"
          sx={{
            p: 0,
            "& .MuiListItemButton-root": {
              mx: 0,
              my: 0,
            },
          }}
        >
          {roleNavigation.map((nav, idx) => {
            const isActive =
              (nav.menu &&
                nav.menu.some((item) =>
                  active?.substring(1) === item?.path ||
                  (item?.path === "claims/bulk-replace" && active?.includes("bulk-replace"))
                )) ||
              active?.substring(1) === nav?.path;
            return (
              <React.Fragment key={idx}>
                <ListItemButton
                  onClick={() => handleNavClick(nav)}
                  className={`group !mx-0 ${isActive ? "active" : ""}`}
                  sx={{
                    py: sidebarOpen ? 0.8 : 1.2,
                    px: sidebarOpen ? 1.0 : 0.5,
                    display: "flex",
                    flexDirection: sidebarOpen ? "row" : "column",
                    alignItems: "center",
                    justifyContent: sidebarOpen ? "initial" : "center",
                    backgroundColor: isActive ? activeBgColor : "transparent",
                    "&:hover": {
                      backgroundColor: isActive ? activeBgColor : hoverBgColor,
                    },
                  }}
                >
                  <ListItemIcon
                    title={nav?.name}
                    className={`rounded-full !min-w-[28px] !w-[28px] !h-[28px] !mx-0 transition-colors ${
                      isActive
                        ? "!text-[#0070C2] !bg-white"
                        : "!text-white !bg-transparent border border-white group-hover:!bg-white group-hover:!border-white group-hover:!text-[#0070C2]"
                    }`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: sidebarOpen ? (isRtl ? 0 : 1) : 0,
                      ml: sidebarOpen ? (isRtl ? 1 : 0) : 0,
                      mb: sidebarOpen ? 0 : 0.5,
                      color: isActive ? "#0070C2" : textColor,
                      "& svg": {
                        fontSize: "0.9rem",
                      },
                    }}
                  >
                    {nav?.icon && <nav.icon />}
                  </ListItemIcon>

                  {sidebarOpen ? (
                    <ListItemText
                      primary={t(nav?.name)}
                      sx={{
                        ml: isRtl ? 0 : 1.25,
                        mr: isRtl ? 1.25 : 0,
                        my: 0,
                        flexGrow: 1,
                        textAlign: isRtl ? "right" : "left",
                        "& .MuiTypography-root": {
                          fontSize: "14px",
                          fontWeight: isActive ? 600 : 400,
                          letterSpacing: "0.3px",
                          whiteSpace: "normal",
                          lineHeight: 1.3,
                          color: textColor,
                        },
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        color: textColor,
                        fontSize: "11px",
                        fontWeight: isActive ? 600 : 500,
                        textAlign: "center",
                        lineHeight: 1.2,
                        wordBreak: "break-word",
                        px: 0.5,
                        maxWidth: "100%",
                      }}
                    >
                      {t(nav?.name)}
                    </Box>
                  )}

                  {sidebarOpen && nav.sidebarNavigations && (
                    <Box
                      sx={{
                        ml: isRtl ? 0 : "auto",
                        mr: isRtl ? "auto" : 0,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {nav.sidebarNavigations.map((subNav, i) => (
                        <Box
                          key={i}
                          onClick={(e) => handleCreateClick(e, subNav)}
                          sx={{
                            color: isActive
                              ? "white"
                              : "rgba(255, 255, 255, 0.7)",
                            cursor: "pointer",
                            display: "flex",
                            "&:hover": {
                              color: "white",
                            },
                          }}
                        >
                          <PlusCircleIcon className="w-5 h-5" />
                        </Box>
                      ))}
                    </Box>
                  )}
                </ListItemButton>
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      {/* Footer Toggle Area */}
      <Box
        sx={{
          borderTop: "1px solid rgba(255,255,255,0.1)",
          // p: 2,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <IconButton
          onClick={() => setSidebarOpen(!sidebarOpen)}
          sx={{ color: "#ffffff" }}
        >
          {sidebarOpen ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>
      </Box>
    </Box>
  );
}

export default SideBar;
